<template>
  <div :class="['m-pdf-viewer', !singlePage && 'm-pdf-viewer--document']" @dragstart.prevent>
    <embed
      v-if="!effectiveIsDemo"
      :src="hideToolbarOnURL(cleanUrl)"
      class="m-pdf-viewer__embed"
      type="application/pdf"
    />
    <template v-else>
      <div v-if="isLoading" class="m-pdf-viewer__status">Loading PDF…</div>
      <div v-else-if="error" class="m-pdf-viewer__status m-pdf-viewer__status--error">
        Failed to load PDF: {{ error.message }}
      </div>
      <!-- ENG-20867 key on the URL so a content change remounts the viewer with a
           fresh registry. In-place document swapping detached the doc-scoped
           capabilities (search/scroll/zoom all went null), breaking auto-scroll
           and highlight after the preview re-fetched the PDF. -->
      <EmbedPDF
        v-else-if="engine"
        :key="cleanUrl"
        :engine="engine"
        :config="registryConfig"
        :plugins="plugins"
      >
        <template #default="{ activeDocumentId, pluginsReady }">
          <div v-if="!pluginsReady" class="m-pdf-viewer__status">Preparing viewer…</div>
          <template v-else-if="activeDocumentId">
            <MPdfViewerDocument
              :document-id="activeDocumentId"
              :url="cleanUrl"
              :single-page="singlePage"
              :selectable="selectable"
              :highlight-target="highlightTarget"
              :show-highlight="showHighlight"
              :auto-scroll="autoScroll"
            />
            <CopyToClipboard v-if="selectable" />
          </template>
        </template>
      </EmbedPDF>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useTenant } from "../../../store/tenant";
import { EmbedPDF } from "@embedpdf/core/vue";
import type { PluginBatchRegistrations } from "@embedpdf/core";
import { usePdfiumEngine } from "@embedpdf/engines/vue";
import { DocumentManagerPluginPackage } from "@embedpdf/plugin-document-manager/vue";
import { ViewportPluginPackage } from "@embedpdf/plugin-viewport/vue";
import { ScrollPluginPackage } from "@embedpdf/plugin-scroll/vue";
import { RenderPluginPackage } from "@embedpdf/plugin-render/vue";
import { ZoomMode, ZoomPluginPackage } from "@embedpdf/plugin-zoom/vue";
import { SearchPluginPackage } from "@embedpdf/plugin-search/vue";
import { InteractionManagerPluginPackage } from "@embedpdf/plugin-interaction-manager/vue";
import { CopyToClipboard, SelectionPluginPackage } from "@embedpdf/plugin-selection/vue";
// ENG-17168 self-host the pdfium WASM — the default is a jsdelivr CDN URL that prod CSP blocks.
import pdfiumWasmAsset from "@embedpdf/pdfium/pdfium.wasm?url";
import MPdfViewerDocument from "./MPdfViewerDocument.vue";

const props = withDefaults(
  defineProps<{
    url: string;
    forceEmbedPdf?: boolean;
    singlePage?: boolean;
    selectable?: boolean;
    highlightTarget?: {
      pageIndex: number;
      x: number;
      y: number;
      width: number;
      height: number;
    } | null;
    showHighlight?: boolean;
    autoScroll?: boolean;
    searchTarget?: string;
  }>(),
  { selectable: true, showHighlight: true, autoScroll: true },
);

const tenantStore = useTenant();
// ENG-20927 prop forces EmbedPDF on specific instances (e.g. template canvas); otherwise driven by tenant store
const effectiveIsDemo = computed(
  () => props.forceEmbedPdf === true || (tenantStore.isDemo ?? false),
);

function hideToolbarOnURL(url: string) {
  return url.includes("#") ? url + "&toolbar=0" : url + "#toolbar=0";
}

// Absolute, same-origin URL so the worker resolves it correctly in dev and prod.
const pdfiumWasmUrl = new URL(pdfiumWasmAsset, window.location.origin).href;
const { engine, isLoading, error } = usePdfiumEngine({ wasmUrl: pdfiumWasmUrl });

const registryConfig = {
  permissions: { enforceDocumentPermissions: true },
};

const cleanUrl = computed(() => {
  const hashIndex = props.url.indexOf("#");
  return hashIndex >= 0 ? props.url.slice(0, hashIndex) : props.url;
});

const plugins = computed<PluginBatchRegistrations>(() => [
  {
    package: DocumentManagerPluginPackage,
    config: {
      initialDocuments: [{ url: cleanUrl.value, autoActivate: true }],
    },
  },
  {
    package: ViewportPluginPackage,
    config: props.singlePage ? { viewportGap: 0 } : {},
  },
  {
    package: ScrollPluginPackage,
    config: props.singlePage ? { defaultPageGap: 0 } : {},
  },
  { package: RenderPluginPackage },
  {
    package: ZoomPluginPackage,
    config: { defaultZoomLevel: props.singlePage ? ZoomMode.FitPage : ZoomMode.FitWidth },
  },
  { package: InteractionManagerPluginPackage },
  { package: SelectionPluginPackage },
  { package: SearchPluginPackage },
]);
</script>

<style scoped>
.m-pdf-viewer {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
  min-width: 0;
  /* EmbedPDF renders page divs with explicit pixel widths that would otherwise
     prevent flex ancestors from shrinking (e.g. on nav sidebar toggle). contain:
     inline-size breaks that dependency — the element sizes from CSS/flex, not
     from its children. ENG-17168 */
  contain: inline-size;
}

/* Document-mode: grey background so the white page is visible, subtle shadow
   around each page image. Excluded from singlePage (template builder canvas
   overlay) where the viewport must be transparent and borderless. */
.m-pdf-viewer--document :deep(.m-pdf-viewer__viewport) {
  background: #f0f0f0;
}

.m-pdf-viewer--document :deep(.m-pdf-viewer__viewport img) {
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}

.m-pdf-viewer__embed {
  flex: 1;
  width: 100%;
  min-height: 0;
}

.m-pdf-viewer__status {
  padding: 24px;
  text-align: center;
  color: var(--text-secondary, #666);
}

.m-pdf-viewer__status--error {
  color: var(--text-error, #c0392b);
}
</style>
